import { Autocomplete } from '@react-google-maps/api';
import { FC, KeyboardEvent, SetStateAction, useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  ButtonColor,
  ButtonSize,
  IconColor,
  IconSize,
  Icons,
  TabsType,
} from '../../enums';
import Button from '../Button';
import strings from '../../language/Swedish';
import Icon from '../Icon';
import useCreateProject from '@/api/mutations/useCreateProject';
import { useNavigate } from 'react-router-dom';
import { PATH } from '@/routes/Paths';
import { useProjectContext } from '@/context/ProjectContext';
import useIsMobile from '@/helpers/useIsMobile';
const Container = styled.div`
  ${tw`absolute flex flex-col items-start gap-2 mt-16 sm:mt-14 ml-2.5 z-10`};
`;

const SearchInputContainer = styled.div<{ $isHidden?: boolean }>`
  ${tw`flex flex-1 items-center bg-white backdrop-blur border border-white w-[240px] rounded-full sm:w-[380px]`};
  margin: -1px;
  ${({ $isHidden }) =>
    $isHidden ? tw`animate-slideOutTop` : tw`animate-slideInTop`};
`;

const SearchWrapper = styled.div`
  ${tw`flex items-center pr-1`};
`;

const ButtonContainer = styled.div<{ $isHidden?: boolean, isMobile?: boolean }>`
  ${tw`flex gap-2`}
    ${({ $isMobile }) => $isMobile && `
        position: fixed;
    top: 58px;
    right: 10px;
    display: flex;
    flex-wrap: wrap;
    width: 100px;
    align-items: flex-end;
    justify-content: flex-end;
    `}
  

    ${({ $isHidden }) =>
    $isHidden ? tw`animate-slideOutTop` : tw`animate-slideInTop`};
`;

const SearchInput = styled.input`
  ${tw`w-full py-1 px-9 text-slate-800 text-sm rounded-l-full truncate`};

  &:focus {
    ${tw`outline-primary ring-emerald-500 border-emerald-500`};
  }
`;

const StyledIcon = styled(Icon)`
  ${tw`absolute left-2 text-slate-400 rotate-180 scale-x-[-1] mx-1`};
`;

interface Props {
  isSearchHidden?: boolean;
  isStreetViewOn: boolean;
  setStreetView: (value: boolean) => void;
  isDrawModeOn: boolean;
  selectedTab: string;
  isAdvancedFilterOpen: boolean;
  onPlaceChanged: (manualAddress?: string | null) => void;
  setAutocomplete: (value: google.maps.places.Autocomplete | null) => void;
  setIsAdvancedFilterOpen: (value: SetStateAction<boolean>) => void;
  startDrawMode: () => void;
  id: string;
}

const SearchContainer: FC<Props> = ({
  isSearchHidden,
  isStreetViewOn,
  selectedTab,
  isDrawModeOn,
  startDrawMode,
  onPlaceChanged,
  setAutocomplete,
  setStreetView,
  isAdvancedFilterOpen,
  setIsAdvancedFilterOpen,
  id
}) => {

  const navigate = useNavigate();
  const { setSelectedTab } = useProjectContext();
  const { mutate: createMutation } = useCreateProject({
    onSuccess: (createData) => {
      if (createData && createData.project) {
        const el = document.getElementById("search-input") as HTMLInputElement;
        if (el) {
          el.value = "";
        }
        const { project } = createData;
        setSelectedTab(TabsType.sold);
        setIsAdvancedFilterOpen(true);
        setStreetView(false);
        navigate(`${PATH.project}/${project.id}`);
      }
    },
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchInputRef.current) {
      onPlaceChanged(searchInputRef.current.value);
    }
  };

  const onClearSearchClick = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      searchInputRef.current.focus();
    }
  };

  const isMobile = useIsMobile();

  return (
    <Container>
      <SearchInputContainer $isHidden={isSearchHidden || isStreetViewOn}>
        <Autocomplete
          className="flex-1"
          restrictions={{ country: 'SE' }}
          onLoad={(ac) => {
            setAutocomplete(ac);
          }}
          onPlaceChanged={() => onPlaceChanged()}>
          <SearchWrapper>
            <SearchInput
              ref={searchInputRef}
              type="text"
              id={id}
              placeholder={strings.data.searchPlaceholder}
              onKeyDown={handleSearchInputKeyDown}
            />
            <StyledIcon
              icon={Icons.search}
              color={IconColor.gray}
              size={IconSize.medium}
            />
            <Button
              icon={Icons.remove}
              size={ButtonSize.small}
              onClick={onClearSearchClick}
            />
          </SearchWrapper>
        </Autocomplete>
      </SearchInputContainer>
      <ButtonContainer $isHidden={isSearchHidden} $isMobile={isMobile}>
        {isStreetViewOn &&
          (<Button
            size={ButtonSize.large}
            color={ButtonColor.warning}
            icon={Icons.chevronLeft}
            onClick={() => setStreetView(false)}
            collapseInMobile
          >
            {strings.data.leaveStreetView}
          </Button>)
        }
        {selectedTab !== TabsType.priceDevelopment && selectedTab !== TabsType.market &&
          (<Button
            size={ButtonSize.large}
            color={isAdvancedFilterOpen ? ButtonColor.secondary : ButtonColor.primary}
            icon={isAdvancedFilterOpen ? Icons.cross : Icons.aboutFilter}
            onClick={() => setIsAdvancedFilterOpen(true)}
            collapseInMobile
          >
            {strings.data.advancedSearch}
          </Button>)
        }
        {selectedTab !== TabsType.priceDevelopment &&
          !isStreetViewOn && (
            <Button
              size={ButtonSize.large}
              color={isDrawModeOn ? ButtonColor.secondary : ButtonColor.primary}
              icon={isDrawModeOn ? Icons.cross : Icons.mapDraw}
              onClick={() => startDrawMode()}
              collapseInMobile
            >
              {strings.data.draw}
            </Button>
          )}
        {isMobile && (
          <Button
            size={ButtonSize.large}
            color={ButtonColor.primary}
            icon={Icons.plus}
            onClick={() => createMutation()}
            collapseInMobile
          />
        )}
      </ButtonContainer>
    </Container>
  );
};

export default SearchContainer;
